import { useTranslation } from 'react-i18next';
import VASValueFeedback from './VASValueFeedback';
import CommentsTable from './CommentsTable';
import OverallImpressionFeedback from './OverallImpressionFeedback';
import AttributeStatsTable from './AttributeStatsTable';
import { useSharedAssessments } from '../../hooks/useSharedAssessments';
import AVIcon from '../../components/icons/AllVoicedIcons';
import resolveAudioUrl from '../../utils/audio/resolveAudioUrl';
import i18n from '../../locales/i18n';
import { resolveCAPEVAdaptation } from '../../activities/cape-v/capevAdaptations';
import {
  getHandmadeEvaluationToolMeta,
  normalizeHandmadeEvaluationToolId,
} from '../../constants/handmadeEvaluationTools';
import { sessionFeedbackRole } from '../../utils/transformSessionFeedback';
import { ApplicationContextualHelp } from '../application';

const SECTION_DEFINITIONS = Object.freeze([
  {
    id: 'standard',
    heading: 'Standard rating items',
    keys: ['overallSeverity', 'roughness', 'breathiness', 'strain'],
    graphKeys: ['overallSeverity', 'roughness', 'breathiness', 'strain'],
    stats: true,
  },
  {
    id: 'pitch',
    headingKey: 'pitch',
    keys: ['normalPitch', 'tooLowPitch', 'tooHighPitch'],
    graphKeys: ['tooLowPitch', 'tooHighPitch'],
    stats: true,
    comments: true,
  },
  {
    id: 'loudness',
    headingKey: 'loudness',
    keys: ['normalLoudness', 'tooQuietLoudness', 'tooLoudLoudness'],
    graphKeys: ['tooQuietLoudness', 'tooLoudLoudness'],
    stats: true,
    comments: true,
  },
  {
    id: 'resonance',
    headingKey: 'resonance',
    keys: ['normalResonance', 'frontResonance', 'backResonance', 'otherResonance'],
    graphKeys: ['frontResonance', 'backResonance', 'otherResonance'],
    stats: true,
    comments: true,
  },
  {
    id: 'nasality',
    headingKey: 'nasality',
    keys: ['normalNasality', 'hyponasality', 'hypernasality'],
    graphKeys: ['hyponasality', 'hypernasality'],
    stats: true,
    comments: true,
  },
  {
    id: 'inconsistencies',
    headingKey: 'inconsistencies',
    keys: ['vowelsInconsistency', 'sentencesInconsistency', 'extemporaneousSpeechInconsistency'],
    graphKeys: [],
    comments: true,
  },
  {
    id: 'instabilities',
    headingKey: 'instabilities',
    keys: ['aphonicBreak', 'pitchBreak', 'pitchInstability', 'spasm', 'tremor', 'otherInstability'],
    graphKeys: ['aphonicBreak', 'pitchBreak', 'pitchInstability', 'spasm', 'tremor', 'otherInstability'],
    stats: true,
    comments: true,
  },
  {
    id: 'additionalFeatures',
    headingKey: 'additional_features',
    keys: ['aphonia', 'asthenia', 'diplophonia', 'falsetto', 'fry', 'hardGlottalAttack', 'wetGurgly', 'otherAdditionalFeature'],
    graphKeys: ['aphonia', 'asthenia', 'diplophonia', 'falsetto', 'fry', 'hardGlottalAttack', 'wetGurgly', 'otherAdditionalFeature'],
    stats: true,
    comments: true,
  },
]);

function getReferencePresentation(assessment, t) {
  const toolMeta = getHandmadeEvaluationToolMeta(assessment?.evaluationTool);

  if (assessment?.raterSource === 'pvqdRater') {
    return {
      role: 'source',
      key: toolMeta?.key === 'capev' ? 'pvqd-cape-v' : 'pvqd',
      label: toolMeta?.key === 'capev'
        ? t('feedback_source_pvqd_cape_v', { defaultValue: 'PVQD CAPE-V observations' })
        : t('feedback_source_pvqd', { defaultValue: 'PVQD source observations' }),
    };
  }

  if (assessment?.raterSource === 'quebecRater') {
    return {
      role: 'source',
      key: 'quebec',
      label: t('feedback_source_quebec', { defaultValue: 'Québec source observations' }),
    };
  }

  return {
    role: 'source',
    key: 'source-linked',
    label: t('feedback_source_generic', { defaultValue: 'Source-linked observations' }),
  };
}

function hasMeaningfulItem(evaluation, key, responseState = 'conditional') {
  const perceptualData = evaluation?.perceptualData;
  if (!perceptualData || !Object.prototype.hasOwnProperty.call(perceptualData, key)) return false;

  const value = perceptualData[key];
  if (!value || typeof value !== 'object') return false;
  if (responseState === 'requiredNumeric') return Number.isFinite(value.VASValue);

  return (
    value.isPresent === true ||
    typeof value.isConsistent === 'boolean' ||
    Number.isFinite(value.VASValue) ||
    (typeof value.attributeComment === 'string' && value.attributeComment.trim() !== '')
  );
}

function hasRenderableValue(evaluation, key) {
  const value = evaluation?.perceptualData?.[key];
  return Boolean(
    value && typeof value === 'object' && (
      Number.isFinite(value.VASValue) ||
      value.isPresent === true ||
      typeof value.isConsistent === 'boolean'
    )
  );
}

function hasComment(evaluation, key) {
  const comment = evaluation?.perceptualData?.[key]?.attributeComment;
  return typeof comment === 'string' && comment.trim() !== '';
}

function hasNumericPopulation(observations, keys) {
  return keys.some((key) => observations.some(
    (observation) => Number.isFinite(observation?.perceptualData?.[key]?.VASValue)
  ));
}

function deduplicateAssessments(assessments) {
  const seenIds = new Set();
  const seenObjects = new Set();

  return assessments.filter((assessment) => {
    const rawId = assessment?._id?._id ?? assessment?._id;
    const id = rawId == null ? null : String(rawId);

    if (id) {
      if (seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    }

    if (seenObjects.has(assessment)) return false;
    seenObjects.add(assessment);
    return true;
  });
}

function getProvenanceLabel(observation, t) {
  if (observation?.raterSource === 'allvoicedUser') {
    return t('feedback_provenance_shared_allvoiced', {
      defaultValue: 'Shared All-Voiced',
    });
  }
  if (observation?.raterSource === 'pvqdRater') return 'PVQD';
  if (observation?.raterSource === 'quebecRater') return 'Québec';
  return t('feedback_provenance_source_linked', {
    defaultValue: 'Source-linked',
  });
}

function buildProvenanceBreakdownByKey(observations, keys, t) {
  return Object.fromEntries(keys.map((key) => {
    const counts = observations.reduce((result, observation) => {
      if (!Number.isFinite(observation?.perceptualData?.[key]?.VASValue)) return result;
      const label = getProvenanceLabel(observation, t);
      result.set(label, (result.get(label) || 0) + 1);
      return result;
    }, new Map());
    const total = Array.from(counts.values()).reduce((sum, count) => sum + count, 0);
    const breakdown = Array.from(counts, ([label, count]) => `${count} ${label}`).join(' · ');

    return [key, total > 0
      ? t(total === 1
          ? 'feedback_comparison_population_breakdown_one'
          : 'feedback_comparison_population_breakdown_other', {
          count: total,
          breakdown,
          defaultValue: `${total} comparison rating${total === 1 ? '' : 's'}: ${breakdown}.`,
        })
      : null];
  }));
}

function FullFeedback({
  apiUrl,
  selectedSample,
  userEvaluation,
  sessionEvaluations = [],
  onGoToPreview,
  onGoToForm,
  isWVD,
  isSessionMode = false,
  isConsensusMode,
  labelT = null,
  capevAdaptationId = null,
  feedbackEvaluationToolId = null,
}) {
  const { t } = useTranslation();
  const resolvedCAPEVAdaptation = capevAdaptationId
    ? resolveCAPEVAdaptation(capevAdaptationId)
    : null;
  const instrumentT = labelT || (resolvedCAPEVAdaptation
    ? i18n.getFixedT(resolvedCAPEVAdaptation.i18nKey)
    : t);
  const normalizedFeedbackToolId = normalizeHandmadeEvaluationToolId(feedbackEvaluationToolId);
  const feedbackToolMeta = getHandmadeEvaluationToolMeta(normalizedFeedbackToolId);
  const presentationContract = feedbackToolMeta?.feedbackPresentationContract || null;
  const statisticsContract = presentationContract?.statistics || null;
  const directSourcePresentation = presentationContract?.directSourcePresentation || null;
  const {
    peerAssessments = [],
    referenceAssessments = [],
    ambiguousReferenceAssessments = [],
    loading,
    error,
  } = useSharedAssessments(
    apiUrl,
    selectedSample?._id,
    normalizedFeedbackToolId ? { evaluationToolId: normalizedFeedbackToolId } : undefined
  );

  const consensusEvaluation = sessionEvaluations.find(
    evaluation => sessionFeedbackRole(evaluation) === 'consensus'
  );
  const participantEvaluations = sessionEvaluations.filter(
    evaluation => sessionFeedbackRole(evaluation) === 'participant'
  );
  const filteredUserEvaluation = isSessionMode ? null : userEvaluation;
  const filteredPeerAssessments = isSessionMode ? [] : peerAssessments;
  const filteredReferenceAssessments = isSessionMode ? [] : referenceAssessments;
  const directPeerAssessments = presentationContract
    ? filteredPeerAssessments.filter(
        (assessment) =>
          normalizeHandmadeEvaluationToolId(assessment.evaluationTool) ===
          presentationContract.directEvidenceToolId
      )
    : filteredPeerAssessments;
  const directReferenceAssessments = presentationContract
    ? deduplicateAssessments(filteredReferenceAssessments.filter(
        (assessment) =>
          normalizeHandmadeEvaluationToolId(assessment.evaluationTool) ===
          presentationContract.directEvidenceToolId
      ))
    : filteredReferenceAssessments;
  const directSourceAssessments = directSourcePresentation
    ? directReferenceAssessments.filter(
        (assessment) =>
          assessment.raterSource === directSourcePresentation.sourceRole &&
          normalizeHandmadeEvaluationToolId(assessment.evaluationTool) ===
            directSourcePresentation.sourceToolId
      )
    : [];
  const statisticsObservations = isSessionMode
    ? participantEvaluations
    : deduplicateAssessments([
        ...directPeerAssessments,
        ...directReferenceAssessments,
      ]);
  const statisticsPopulationKind = isSessionMode
    ? 'participants'
    : 'same-instrument-direct';
  const sharedAllVoicedLegendHelp = !isSessionMode ? (
    <ApplicationContextualHelp
      label={t('feedback_shared_allvoiced_help_label', {
        defaultValue: 'Who are these ratings from?',
      })}
      title={t('feedback_shared_allvoiced_help_title', {
        defaultValue: 'About Shared All-Voiced ratings',
      })}
    >
      <p>{t('feedback_shared_allvoiced_help_body', {
        toolName: feedbackToolMeta?.label,
        defaultValue: `These are ratings of the same voice sample shared for feedback by other All-Voiced users who identify themselves as advanced or expert and used the same ${feedbackToolMeta?.label || ''} rating method. They chose to share their rating for feedback. The comparison is descriptive, not a correct answer, and self-declared level is not credential verification.`.trim(),
      })}</p>
    </ApplicationContextualHelp>
  ) : null;
  const primaryReferenceAssessments = directSourcePresentation
    ? directReferenceAssessments.filter(
        (assessment) => !directSourceAssessments.includes(assessment)
      )
    : directReferenceAssessments;
  const peerPresentation = directPeerAssessments.map((assessment) => ({
    ...assessment,
    feedbackPresentation: {
      role: 'peer',
      key: 'allvoiced-peer',
      label: feedbackToolMeta
        ? t('feedback_peer_evaluations', {
            toolName: feedbackToolMeta.label,
            defaultValue: `${feedbackToolMeta.label} peer evaluations`,
          })
        : t('other_allvoiced_users_evaluations'),
      help: sharedAllVoicedLegendHelp,
    },
  }));
  const directReferencePresentation = primaryReferenceAssessments.map((assessment) => ({
    ...assessment,
    feedbackPresentation: getReferencePresentation(assessment, t),
  }));
  const primaryDisplayAssessments = [...peerPresentation, ...directReferencePresentation];
  const modeEvidence = isSessionMode
    ? [...participantEvaluations, ...(consensusEvaluation ? [consensusEvaluation] : [])]
    : filteredUserEvaluation ? [filteredUserEvaluation] : [];

  const getEligibleSectionKeys = (definition) => {
    if (!presentationContract) return definition.keys;
    const contractItems = presentationContract.sections[definition.id];
    if (!contractItems) return [];

    return definition.keys.filter((key) => {
      const responseState = contractItems[key]?.responseState;
      return responseState && modeEvidence.some(
        (evaluation) => hasMeaningfulItem(evaluation, key, responseState)
      );
    });
  };

  const hasSectionContent = (keys, definition, commentsEnabled) => {
    const renderPopulation = [
      ...(filteredUserEvaluation ? [filteredUserEvaluation] : []),
      ...primaryDisplayAssessments,
      ...participantEvaluations,
      ...(consensusEvaluation ? [consensusEvaluation] : []),
    ];
    const commentPopulation = [
      ...(filteredUserEvaluation ? [filteredUserEvaluation] : []),
      ...directPeerAssessments,
      ...participantEvaluations,
      ...(consensusEvaluation ? [consensusEvaluation] : []),
    ];

    if (!presentationContract) {
      return keys.some((key) => renderPopulation.some((evaluation) => {
        const value = evaluation?.perceptualData?.[key];
        if (!value || typeof value !== 'object') return false;

        return Object.entries(value).some(([field, fieldValue]) => {
          if (field === 'attributeName' || field === '_id') return false;
          if (typeof fieldValue === 'string') return fieldValue.trim() !== '';
          if (typeof fieldValue === 'number') return field !== 'VASValue' || fieldValue > 0;
          return fieldValue === true;
        });
      }));
    }

    const hasValueRendering = definition.stats || definition.graphKeys.some(
      (key) => keys.includes(key)
    );

    return keys.some((key) =>
      (hasValueRendering && renderPopulation.some((evaluation) => hasRenderableValue(evaluation, key))) ||
      (commentsEnabled && commentPopulation.some((evaluation) => hasComment(evaluation, key)))
    );
  };

  const supportsComments = presentationContract?.supportsComments ?? true;
  const supportsOverallImpression = presentationContract?.supportsOverallImpression ?? true;
  const hasOverallImpression = supportsOverallImpression && [
    ...(filteredUserEvaluation ? [filteredUserEvaluation] : []),
    ...directPeerAssessments,
    ...(!presentationContract ? directReferenceAssessments : []),
    ...participantEvaluations,
    ...(consensusEvaluation ? [consensusEvaluation] : []),
  ].some((evaluation) => Boolean(evaluation?.perceptualData?.overallImpression));

  const contextPermission = !isSessionMode
    ? presentationContract?.secondaryContext || directSourcePresentation
    : null;
  const contextAssessments = contextPermission
    ? deduplicateAssessments(filteredReferenceAssessments.filter((assessment) =>
        assessment.raterSource === contextPermission.sourceRole &&
        normalizeHandmadeEvaluationToolId(assessment.evaluationTool) === contextPermission.sourceToolId
      ))
    : [];
  const contractItemEntries = presentationContract
    ? Object.values(presentationContract.sections).flatMap((section) => Object.entries(section))
    : [];
  const contextCandidateItems = Array.isArray(contextPermission?.itemScope)
    ? contextPermission.itemScope.map((key) => [
        key,
        contractItemEntries.find(([itemKey]) => itemKey === key)?.[1],
      ])
    : contractItemEntries;
  const contextKeys = contextPermission
    ? contextCandidateItems
        .filter(([key, itemConfig]) =>
          itemConfig &&
          hasMeaningfulItem(filteredUserEvaluation, key, itemConfig.responseState) &&
          contextAssessments.some(
            (assessment) => Number.isFinite(assessment?.perceptualData?.[key]?.VASValue)
          )
        )
        .map(([key]) => key)
    : [];
  const isDirectCapeVSourceContext = Boolean(directSourcePresentation);
  const contextLegendLabel = isDirectCapeVSourceContext
    ? t('feedback_source_pvqd_cape_v', { defaultValue: 'PVQD CAPE-V observations' })
    : t('feedback_context_pvqd_cape_v_compact', {
        defaultValue: 'PVQD CAPE-V context',
      });
  const contextLegendHelp = contextPermission && contextKeys.length ? (
    <ApplicationContextualHelp
      label={t(contextPermission.helpLabelKey, {
        defaultValue: 'About the PVQD observations',
      })}
      title={isDirectCapeVSourceContext
        ? t('feedback_context_pvqd_cape_v_capev_help_title', {
            defaultValue: 'About the PVQD CAPE-V observations',
          })
        : contextLegendLabel}
    >
      <p>{t(contextPermission.helpBodyKey)}</p>
    </ApplicationContextualHelp>
  ) : null;
  const contextPresentationAssessments = contextAssessments.map((assessment) => ({
    ...assessment,
    perceptualData: Object.fromEntries(
      Object.entries(assessment.perceptualData || {}).filter(([key]) => contextKeys.includes(key))
    ),
    feedbackPresentation: {
      role: 'source',
      key: 'pvqd-cape-v-context',
      label: contextLegendLabel,
      help: contextLegendHelp,
    },
  }));
  const contextStatisticsContract = isDirectCapeVSourceContext
    ? null
    : contextPermission?.statistics || null;
  const contextStatisticsObservations = isDirectCapeVSourceContext
    ? directSourceAssessments
    : contextAssessments;
  const audioSrc = resolveAudioUrl(selectedSample?.sampleLink);
  const ratingItemLabel = 'Rating item';
  const statisticsCaption = isSessionMode
    ? t('feedback_participant_population_label', {
        defaultValue: 'Direct participant ratings',
      })
    : t('feedback_shared_allvoiced_ratings', {
        defaultValue: 'Shared All-Voiced ratings',
      });
  const directStatisticsCaption = t('feedback_same_instrument_comparison_caption', {
    toolName: feedbackToolMeta?.label,
    defaultValue: `Available ${feedbackToolMeta?.label || ''} comparison ratings`.trim(),
  });
  const summaryStatisticsHelp = isSessionMode ? (
    <ApplicationContextualHelp
      label={t('feedback_summary_statistics_help_label', {
        defaultValue: 'How are these summary statistics calculated?',
      })}
      title={t('feedback_summary_statistics_help_title', {
        defaultValue: 'About these summary statistics',
      })}
    >
      <p>{t('feedback_summary_statistics_session_help_body', {
        defaultValue: 'Each n is the number of direct participant ratings for that item. Submitted consensus and derived aggregates are excluded. Five-number summaries are displayed from n=5 and describe Min, Q1, Median, Q3, and Max; they do not establish correctness.',
      })}</p>
    </ApplicationContextualHelp>
  ) : (
    <ApplicationContextualHelp
      label={t('feedback_summary_statistics_help_label', {
        defaultValue: 'How are these summary statistics calculated?',
      })}
      title={t('feedback_summary_statistics_help_title', {
        defaultValue: 'About these summary statistics',
      })}
    >
      <p>{t('feedback_summary_statistics_help_body', {
        defaultValue: 'The direct comparison summary includes eligible observations made with the same rating instrument, potentially from different provenance populations whose composition is shown explicitly. Your rating and cross-instrument contextual observations are excluded. Each n is the number included for that item; five-number summaries are displayed from n=5 and describe Min, Q1, Median, Q3, and Max. Separate contextual summaries, where present, remain independent. The summaries are descriptive and do not establish correctness.',
      })}</p>
    </ApplicationContextualHelp>
  );
  const contextStatisticsCaption = contextLegendLabel;

  return (
    <div className="feedback-view">
      <h1>{instrumentT('feedback')}</h1>

      {loading ? (
        <p>{instrumentT('loading_shared_evaluations')}</p>
      ) : error ? (
        <p>{instrumentT('error_loading_shared_evaluations')}</p>
      ) : (
        <>
          <div className="app-module-section">
            {selectedSample ? (
              <div className="app-module-audio-container">
                <h2>{instrumentT('sample')}: {selectedSample.sampleName || selectedSample.name}</h2>
                {selectedSample.sampleLink ? (
                  <audio controls src={audioSrc}></audio>
                ) : (
                  <div className="warning-message">
                    <AVIcon icon="alert" size={36} color="#209385" />
                    {instrumentT('no_audio_available')}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {SECTION_DEFINITIONS.map((definition) => {
            const eligibleKeys = getEligibleSectionKeys(definition);
            const commentsEnabled = definition.comments && supportsComments;
            if (!eligibleKeys.length || !hasSectionContent(eligibleKeys, definition, commentsEnabled)) return null;
            const graphKeys = definition.graphKeys.filter((key) => eligibleKeys.includes(key));
            const contextSummaryKeys = eligibleKeys.filter((key) => contextKeys.includes(key));
            const provenanceBreakdownByKey = isSessionMode
              ? null
              : buildProvenanceBreakdownByKey(statisticsObservations, eligibleKeys, t);
            const showPrimaryStatistics = Boolean(
              definition.stats &&
              statisticsContract &&
              hasNumericPopulation(statisticsObservations, eligibleKeys)
            );
            const showContextStatistics = Boolean(
              definition.stats &&
              contextStatisticsContract &&
              contextSummaryKeys.length &&
              hasNumericPopulation(contextStatisticsObservations, contextSummaryKeys)
            );
            const suppressHeading = feedbackToolMeta?.key === 'overall-severity' && definition.id === 'standard';
            const showDirectionalSourceHelp =
              feedbackToolMeta?.key === 'capev' &&
              ['pitch', 'loudness'].includes(definition.id);

            return (
              <div
                key={definition.id}
                data-feedback-section={definition.id}
                data-testid={`feedback-section-${definition.id}`}
              >
                {!suppressHeading ? <h3>{definition.heading || instrumentT(definition.headingKey)}</h3> : null}
                {showDirectionalSourceHelp ? (
                  <div className="catalog-detail__section-intro">
                    <ApplicationContextualHelp
                      label={t('feedback_cape_v_direction_help_label', {
                        defaultValue: 'Why are some source ratings not shown here?',
                      })}
                      title={t('feedback_cape_v_direction_help_title', {
                        defaultValue: 'About source comparison for pitch and loudness',
                      })}
                    >
                      <p>{t('feedback_cape_v_direction_help_body', {
                        defaultValue: 'Available source ratings do not distinguish pitch or loudness direction in the same way as this CAPE-V rating. Because high versus low pitch and loud versus quiet ratings are recorded separately here, those source observations are not shown as directly comparable feedback.',
                      })}</p>
                    </ApplicationContextualHelp>
                  </div>
                ) : null}
                {graphKeys.length ? (
                  <VASValueFeedback
                    userData={filteredUserEvaluation}
                    assessments={[
                      ...primaryDisplayAssessments,
                      ...contextPresentationAssessments,
                    ]}
                    sessionEvaluations={participantEvaluations}
                    consensusEvaluation={consensusEvaluation}
                    attributeKeys={graphKeys}
                    isSessionMode={isSessionMode}
                    labelT={instrumentT}
                    allVoicedLegendLabel={t('feedback_shared_allvoiced_ratings', {
                      defaultValue: 'Shared All-Voiced ratings',
                    })}
                  />
                ) : null}
                {showPrimaryStatistics || showContextStatistics ? (
                  <div className="feedback-summary-region">
                    <div
                      className="catalog-detail__section-intro"
                      style={{ alignItems: 'center', display: 'flex', gap: '0.5rem' }}
                    >
                      <strong>{t('feedback_summary_statistics_heading', {
                        defaultValue: 'Summary statistics',
                      })}</strong>
                      {summaryStatisticsHelp}
                    </div>
                    {showPrimaryStatistics ? (
                      <AttributeStatsTable
                        mode="numeric-summary"
                        observations={statisticsObservations}
                        minimumN={statisticsContract.minimumN}
                        populationKind={statisticsPopulationKind}
                        caption={isSessionMode ? statisticsCaption : directStatisticsCaption}
                        provenanceBreakdownByKey={provenanceBreakdownByKey}
                        attributeKeys={eligibleKeys}
                        labelT={instrumentT}
                        attributeLabel={ratingItemLabel}
                      />
                    ) : null}
                    {showContextStatistics ? (
                      <AttributeStatsTable
                        mode="numeric-summary"
                        observations={contextStatisticsObservations}
                        minimumN={contextStatisticsContract.minimumN}
                        populationKind="pvqd-cape-v"
                        caption={contextStatisticsCaption}
                        attributeKeys={contextSummaryKeys}
                        labelT={instrumentT}
                        attributeLabel={ratingItemLabel}
                      />
                    ) : null}
                  </div>
                ) : null}
                {commentsEnabled ? (
                  <>
                    <br />
                    <CommentsTable
                      userData={filteredUserEvaluation}
                      assessments={directPeerAssessments}
                      sessionEvaluations={participantEvaluations}
                      consensusEvaluation={consensusEvaluation}
                      attributeKeys={eligibleKeys}
                      isSessionMode={isSessionMode}
                      labelT={instrumentT}
                      attributeLabel={ratingItemLabel}
                    />
                  </>
                ) : null}
              </div>
            );
          })}

          {hasOverallImpression ? (
            <>
              <h3>{instrumentT('overall_impression')}</h3>
              <OverallImpressionFeedback
                userData={filteredUserEvaluation}
                assessments={directPeerAssessments}
                sessionEvaluations={participantEvaluations}
                consensusEvaluation={consensusEvaluation}
                isSessionMode={isSessionMode}
                labelT={instrumentT}
              />
            </>
          ) : null}

          {!presentationContract && ambiguousReferenceAssessments.length > 0 ? (
            <p className="small">
              Some reference-source evaluations could not be fully classified by evaluation tool and were kept outside peer statistics.
            </p>
          ) : null}

          <br />
          <div className="app-btn-container">
            {isWVD ? (
              <div className="app-btn-container">
                <button onClick={() => { window.location.href = '/app'; }} className="app-btn app-btn-primary">
                  {t('evaluate_another_voice', 'Evaluate another voice')}
                </button>
                <button onClick={() => window.open('/wvd-event', '_blank')} className="app-btn app-btn-secondary">
                  {t('see_live_event', 'See live event progress')}
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default FullFeedback;
